import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu,
  X,
  Home,
  FileText,
  Users,
  BarChart3,
  LogOut,
  Search,
  Workflow,
  UserCircle,
  Settings,
  ChevronDown,
  MapPin,
  XCircle,
  FileEdit,
  Megaphone
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
    { name: 'Permits', href: '/permits', icon: FileText, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
    { name: 'Workflow', href: '/workflow', icon: Workflow, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
    { name: 'Edit Requests', href: '/edit-requests', icon: FileEdit, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
    { name: 'Cancellation Requests', href: '/cancellation-requests', icon: XCircle, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
    { name: 'Circulars', href: '/announcements', icon: Megaphone, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
    // Users menu for ADC (subdivision-restricted user management)
    { name: 'Users', href: '/users', icon: Users, roles: ['adc'] },
    {
      name: 'Administration',
      icon: Settings,
      roles: ['dc'],
      children: [
        { name: 'Users', href: '/users', icon: Users },
        { name: 'Subdivisions', href: '/admin/subdivisions', icon: MapPin },
      ]
    },
    { name: 'Reports', href: '/reports', icon: BarChart3, roles: ['eac', 'sdo', 'adc', 'dc'] },
    { name: 'Public Search', href: '/search', icon: Search, roles: ['bo', 'eac', 'sdo', 'adc', 'dc'] },
  ];

  const filteredNavigation = navigation.filter(item => 
    item.roles.includes(user?.role)
  );

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-secondary-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <nav className="fixed top-0 left-0 bottom-0 flex flex-col w-5/6 max-w-sm bg-white border-r border-secondary-200">
          <div className="flex items-center justify-between flex-shrink-0 px-4 py-4 border-b border-secondary-200">
            <div className="flex items-center space-x-3">
              <img
                className="h-10 w-10 object-contain"
                src="https://cdn.s3waas.gov.in/s368264bdb65b97eeae6788aa3348e553c/uploads/2018/04/2018040790.jpg"
                alt="Government Logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <h1 className="text-lg font-semibold text-secondary-900">Mon Land Portal</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 text-secondary-400 hover:text-secondary-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 px-4 py-4 overflow-y-auto">
            <SidebarContent 
              navigation={filteredNavigation} 
              currentPath={location.pathname}
              user={user}
              onLogout={handleLogout}
            />
          </div>
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-300 ${
        desktopSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <nav className="flex flex-col flex-grow bg-white border-r border-secondary-200">
          <div className="flex items-center justify-between flex-shrink-0 px-4 py-4 border-b border-secondary-200">
            {!desktopSidebarCollapsed ? (
              <div className="flex items-center space-x-3">
                <img
                  className="h-10 w-10 object-contain"
                  src="https://cdn.s3waas.gov.in/s368264bdb65b97eeae6788aa3348e553c/uploads/2018/04/2018040790.jpg"
                  alt="Government Logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <h1 className="text-md font-bold text-primary-600">Mon Land Portal</h1>
              </div>
            ) : (
              <img
                className="h-10 w-10 object-contain mx-auto"
                src="https://cdn.s3waas.gov.in/s368264bdb65b97eeae6788aa3348e553c/uploads/2018/04/2018040790.jpg"
                alt="Government Logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <button
              onClick={() => setDesktopSidebarCollapsed(!desktopSidebarCollapsed)}
              className="p-1 text-secondary-400 hover:text-secondary-500"
              title={desktopSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
          <div className={`flex-1 py-4 overflow-y-auto ${desktopSidebarCollapsed ? 'px-2' : 'px-4'}`}>
            <SidebarContent 
              navigation={filteredNavigation} 
              currentPath={location.pathname}
              user={user}
              onLogout={handleLogout}
              collapsed={desktopSidebarCollapsed}
            />
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${
        desktopSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
      }`}>
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-secondary-200">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1 text-secondary-400 lg:hidden hover:text-secondary-500"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center space-x-4">
              <img
                className="h-10 w-auto object-contain"
                src="https://www.monsmarthub.com/assets/img/logo.png"
                alt="SMART Hub Logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="text-sm text-secondary-600">
                Welcome, <span className="font-medium text-secondary-900">{user?.fullName}</span>
              </div>
              <div className="text-xs text-secondary-500 bg-secondary-100 px-2 py-1 rounded">
                {user?.role === 'bo' && 'Branch Officer'}
                {user?.role === 'eac' && 'EAC'}
                {user?.role === 'sdo' && 'SDO'}
                {user?.role === 'adc' && 'ADC'}
                {user?.role === 'dc' && 'DC'}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarContent = ({ navigation, currentPath, user, onLogout, collapsed }) => {
  const isProfileActive = currentPath === '/profile';

  // Track which dropdown is expanded
  const [expandedItem, setExpandedItem] = useState(null);

  // Auto-expand dropdown if current path matches any child
  useEffect(() => {
    navigation.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          (child) => currentPath === child.href || currentPath.startsWith(child.href + '/')
        );
        if (hasActiveChild) {
          setExpandedItem(item.name);
        }
      }
    });
  }, [currentPath, navigation]);

  const toggleExpand = (itemName) => {
    setExpandedItem(expandedItem === itemName ? null : itemName);
  };

  return (
    <div className="space-y-1">
      {navigation.map((item) => {
        // Check if this is a dropdown item (has children)
        if (item.children) {
          const isExpanded = expandedItem === item.name;
          const hasActiveChild = item.children.some(
            (child) => currentPath === child.href || currentPath.startsWith(child.href + '/')
          );

          return (
            <div key={item.name}>
              {/* Dropdown toggle button */}
              <button
                onClick={() => toggleExpand(item.name)}
                className={`group flex w-full items-center text-sm font-medium rounded-md ${
                  hasActiveChild
                    ? 'bg-primary-50 text-primary-900'
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                } ${collapsed ? 'justify-center p-3 mx-1' : 'px-2 py-2'}`}
                title={collapsed ? item.name : ''}
              >
                <item.icon
                  className={`${collapsed ? 'h-6 w-6' : 'mr-3 h-5 w-5'} ${
                    hasActiveChild ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'
                  }`}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.name}</span>
                    <ChevronDown
                      className={`h-4 w-4 text-secondary-400 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </>
                )}
              </button>

              {/* Dropdown children */}
              {!collapsed && isExpanded && (
                <div className="mt-1 ml-4 space-y-1">
                  {item.children.map((child) => {
                    const isChildActive = currentPath === child.href || currentPath.startsWith(child.href + '/');
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={`group flex items-center text-sm font-medium rounded-md pl-4 py-2 ${
                          isChildActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                        }`}
                      >
                        <child.icon
                          className={`mr-3 h-4 w-4 ${
                            isChildActive ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'
                          }`}
                        />
                        {child.name}
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Collapsed mode - show children on hover/click */}
              {collapsed && isExpanded && (
                <div className="absolute left-full ml-2 bg-white shadow-lg rounded-md py-2 min-w-[160px] z-50">
                  {item.children.map((child) => {
                    const isChildActive = currentPath === child.href || currentPath.startsWith(child.href + '/');
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={`group flex items-center text-sm font-medium px-4 py-2 ${
                          isChildActive
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                        }`}
                      >
                        <child.icon
                          className={`mr-3 h-4 w-4 ${
                            isChildActive ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'
                          }`}
                        />
                        {child.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        // Regular navigation item (no children)
        const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/');
        return (
          <Link
            key={item.name}
            to={item.href}
            className={`group flex items-center text-sm font-medium rounded-md ${
              isActive
                ? 'bg-primary-100 text-primary-900'
                : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
            } ${collapsed ? 'justify-center p-3 mx-1' : 'px-2 py-2'}`}
            title={collapsed ? item.name : ''}
          >
            <item.icon
              className={`${collapsed ? 'h-6 w-6' : 'mr-3 h-5 w-5'} ${
                isActive ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'
              }`}
            />
            {!collapsed && item.name}
          </Link>
        );
      })}

      <div className="pt-4 mt-4 border-t border-secondary-200">
        <Link
          to="/profile"
          className={`group flex items-center text-sm font-medium rounded-md ${
            isProfileActive
              ? 'bg-primary-100 text-primary-900'
              : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
          } ${collapsed ? 'justify-center p-3 mx-1' : 'px-2 py-2'}`}
          title={collapsed ? 'My Profile' : ''}
        >
          <UserCircle
            className={`${collapsed ? 'h-6 w-6' : 'mr-3 h-5 w-5'} ${
              isProfileActive ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'
            }`}
          />
          {!collapsed && 'My Profile'}
        </Link>

        <button
          onClick={onLogout}
          className={`group flex w-full items-center text-sm font-medium text-secondary-600 rounded-md hover:bg-secondary-50 hover:text-secondary-900 ${
            collapsed ? 'justify-center p-3 mx-1' : 'px-2 py-2'
          }`}
          title={collapsed ? 'Sign out' : ''}
        >
          <LogOut className={`${collapsed ? 'h-6 w-6' : 'mr-3 h-5 w-5'} text-secondary-400 group-hover:text-secondary-500`} />
          {!collapsed && 'Sign out'}
        </button>
      </div>
    </div>
  );
};

export default Layout;